import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate, Link } from 'react-router-dom';

const TermsPage = () => {
  const navigate = useNavigate();
  const lastUpdated = 'February 25, 2026';

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      {/* Navigation */}
      <nav className="glass-card border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex justify-between items-center">
          <Link to="/" className="font-playfair text-2xl sm:text-3xl font-semibold text-gold">ASTIIV BASGAT</Link>
          <Button 
            variant="ghost" 
            className="text-white/60 hover:text-white"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-2xl bg-gold/20 flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4">Terms of Service</h1>
            <p className="text-text-secondary">Last updated: {lastUpdated}</p>
          </div>

          {/* Content */}
          <div className="prose prose-invert prose-gold max-w-none space-y-8">
            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">1. Acceptance of Terms</h2>
              <p className="text-text-secondary leading-relaxed">
                Welcome to ASTIIV BASGAT. By accessing or using our platform, website, mobile applications, and services 
                (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). 
                ASTIIV BASGAT LLC is a company registered in the United Arab Emirates, operating in accordance with 
                UAE Federal Laws and the regulations of the Dubai Department of Economy and Tourism.
              </p>
              <p className="text-text-secondary leading-relaxed mt-4">
                If you do not agree to these Terms, please do not access or use our Services. These Terms constitute 
                a legally binding agreement between you and ASTIIV BASGAT LLC.
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">2. Description of Services</h2>
              <p className="text-text-secondary leading-relaxed">
                ASTIIV BASGAT provides an ultra-luxury lifestyle and real estate intelligence platform that includes:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li>Property discovery and listing services for luxury real estate in Dubai and the UAE</li>
                <li>AI-powered lifestyle recommendations and matching services</li>
                <li>Marketplace for luxury goods including vehicles, timepieces, and accessories</li>
                <li>Partner portal for real estate developers, agents, and luxury brands</li>
                <li>Investment analysis tools and ROI calculators</li>
                <li>Concierge services and personalized assistance</li>
              </ul>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">3. User Registration and Accounts</h2>
              <p className="text-text-secondary leading-relaxed">
                To access certain features of our Services, you must register for an account. When registering, you agree to:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your password secure and confidential</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized access</li>
              </ul>
              <p className="text-text-secondary leading-relaxed mt-4">
                You must be at least 21 years of age or the legal age of majority in your jurisdiction to use our Services.
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">4. Subscription Plans and Payments</h2>
              <p className="text-text-secondary leading-relaxed">
                ASTIIV BASGAT offers various subscription tiers with different features and pricing:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li><strong>Free Tier:</strong> Basic access to property listings and marketplace</li>
                <li><strong>Premium Tier:</strong> Enhanced features including AI recommendations and analytics</li>
                <li><strong>Elite Tier:</strong> Full access to all features including concierge services</li>
              </ul>
              <p className="text-text-secondary leading-relaxed mt-4">
                All payments are processed securely through our payment partners. Prices are displayed in AED (UAE Dirhams) 
                unless otherwise specified. Subscriptions auto-renew unless cancelled before the renewal date. 
                Refunds are subject to our refund policy and UAE Consumer Protection Laws.
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">5. Property Listings and Transactions</h2>
              <p className="text-text-secondary leading-relaxed">
                ASTIIV BASGAT acts as a platform connecting buyers with sellers and agents. We do not own, manage, 
                or have control over any properties listed on our platform. All property transactions are subject to:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li>Dubai Land Department (DLD) regulations and procedures</li>
                <li>Real Estate Regulatory Agency (RERA) guidelines</li>
                <li>UAE Federal Laws governing property transactions</li>
                <li>Independent due diligence by all parties</li>
              </ul>
              <p className="text-text-secondary leading-relaxed mt-4">
                ASTIIV BASGAT does not guarantee the accuracy of property listings and is not responsible for 
                any disputes arising from property transactions.
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">6. Partner Responsibilities</h2>
              <p className="text-text-secondary leading-relaxed">
                Partners using our platform (developers, agents, dealers) agree to:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li>Maintain valid UAE trade licenses and professional certifications</li>
                <li>Provide accurate and truthful information about listings</li>
                <li>Comply with RERA regulations and advertising guidelines</li>
                <li>Respond to customer inquiries within reasonable timeframes</li>
                <li>Pay all applicable fees, including cost-per-lead charges</li>
              </ul>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">7. Intellectual Property</h2>
              <p className="text-text-secondary leading-relaxed">
                All content, features, and functionality of our Services, including but not limited to text, 
                graphics, logos, icons, images, audio clips, digital downloads, and software, are the exclusive 
                property of ASTIIV BASGAT LLC and are protected by UAE and international copyright, trademark, 
                and other intellectual property laws.
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">8. Limitation of Liability</h2>
              <p className="text-text-secondary leading-relaxed">
                To the fullest extent permitted by UAE law, ASTIIV BASGAT LLC shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages, including but not limited to loss of 
                profits, data, use, goodwill, or other intangible losses, resulting from:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li>Your access to or use of or inability to access or use the Services</li>
                <li>Any conduct or content of any third party on the Services</li>
                <li>Any content obtained from the Services</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content</li>
              </ul>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">9. Governing Law and Disputes</h2>
              <p className="text-text-secondary leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates. 
                Any disputes arising from these Terms or the use of our Services shall be subject to the exclusive 
                jurisdiction of the courts of Dubai, UAE.
              </p>
              <p className="text-text-secondary leading-relaxed mt-4">
                Before initiating any legal proceedings, parties agree to attempt to resolve disputes through 
                good-faith negotiation and, if necessary, mediation through the Dubai International Arbitration Centre (DIAC).
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">10. Contact Information</h2>
              <p className="text-text-secondary leading-relaxed">
                For questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-4 text-text-secondary">
                <p><strong>ASTIIV BASGAT LLC</strong></p>
                <p>Dubai, United Arab Emirates</p>
                <p>Email: legal@dreamlife.ae</p>
                <p>Phone: +971 4 XXX XXXX</p>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-12 text-center">
            <Button 
              onClick={() => navigate('/')}
              className="bg-gold text-black hover:bg-gold-light"
            >
              Back to Home
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsPage;
